import React, { useState } from "react";
import { Box, TextField, Button, List, ListItemButton, ListItemText, Typography } from "@mui/material";
import { Search } from "lucide-react";
import { sanitizePlaceQuery, searchPlaces } from "./geocodingService";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";

export const GeocodingSearch = ({ onSelectPlace = () => {} }) => {
  const t = useScrollytellTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSearch = async () => {
    const sanitized = sanitizePlaceQuery ? sanitizePlaceQuery(query) : query;
    const clean = (typeof sanitized === "string" && sanitized ? sanitized : (query ? String(query).trim() : "")).trim();
    if (!clean) return;

    setLoading(true);
    setErrorMessage("");

    const response = await searchPlaces(clean);
    setLoading(false);

    if (response.error) {
      setErrorMessage(response.error);
      setResults([]);
    } else {
      setResults(response.results || []);
      if (response.results.length === 0) {
        setErrorMessage(t("geocodingSearch.noPlacesFound", "Ingen steder funnet for dette søket."));
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSelect = (place) => {
    onSelectPlace(place);
    setQuery("");
    setResults([]);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("geocodingSearch.placeholder", "f.eks. Røros, Bergen, Lerwick...")}
          inputProps={{
            role: "searchbox",
            "aria-label": t("geocodingSearch.ariaLabel", "Søk etter sted"),
          }}
          sx={{
            "& .MuiInputBase-root": {
              backgroundColor: "#FFFFFF",
              color: "#1E293B",
              fontSize: "13px",
            },
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSearch}
          disabled={loading}
          aria-label={t("geocodingSearch.findPlace", "Finn sted")}
          sx={{
            backgroundColor: "#0284C7",
            minWidth: "44px",
            height: "40px",
            "&:hover": { backgroundColor: "#0369A1" },
          }}
        >
          <Search size={18} />
        </Button>
      </Box>

      {/* Screen reader & error feedback */}
      <div role="status" aria-live="polite">
        {errorMessage && (
          <Typography variant="caption" sx={{ color: "#EF4444", fontSize: "12px", display: "block", mt: 0.5 }}>
            {errorMessage}
          </Typography>
        )}
      </div>

      {results.length > 0 && (
        <List
          sx={{
            maxHeight: 180,
            overflowY: "auto",
            backgroundColor: "#FFFFFF",
            borderRadius: "4px",
            border: "1px solid #CBD5E1",
            p: 0,
          }}
        >
          {results.map((place) => (
            <ListItemButton
              key={place.id}
              onClick={() => handleSelect(place)}
              sx={{
                borderBottom: "1px solid #E2E8F0",
                "&:hover": { backgroundColor: "#F1F5F9" },
                py: 0.5,
              }}
            >
              <ListItemText
                primary={place.title}
                secondary={place.subtitle}
                primaryTypographyProps={{ sx: { color: "#1E293B", fontSize: "13px", fontWeight: 600 } }}
                secondaryTypographyProps={{ sx: { color: "#64748B", fontSize: "11px" } }}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
};
